/**
 * MAHI — ambient fashion backdrop.
 *
 * A field of faint, slowly drifting fashion glyphs (garments, a model
 * silhouette, accessories) sits behind a translucent white scrim so the
 * page still reads as clean and white, but conveys "fashion tech" rather
 * than a blank form. Motion is intentionally subtle — slow float + a few
 * degrees of rotation, nothing that competes with the foreground card.
 *
 * Respects prefers-reduced-motion by freezing the glyphs in place.
 */
import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Shirt, PersonStanding, Gem, Watch, Sparkles, Glasses, ShoppingBag, Wand2, SwatchBook } from 'lucide-react';

const GLYPHS = [
  { Icon: Shirt,          top: '8%',  left: '6%',   size: 70, opacity: 0.50, color: 'text-slate-500', duration: 12, delay: 0,   drift: 18, rotate: 6 },
  { Icon: PersonStanding, top: '16%', left: '86%',  size: 94, opacity: 0.42, color: 'text-slate-500', duration: 15, delay: 1,   drift: 20, rotate: 4 },
  { Icon: Gem,            top: '76%', left: '10%',  size: 46, opacity: 0.55, color: 'text-amber-500', duration: 9,  delay: 0.5, drift: 14, rotate: 8 },
  { Icon: Watch,          top: '54%', left: '3%',   size: 52, opacity: 0.46, color: 'text-slate-500', duration: 11, delay: 2,   drift: 16, rotate: 6 },
  { Icon: Sparkles,       top: '10%', left: '68%',  size: 40, opacity: 0.60, color: 'text-cyan-500',  duration: 7,  delay: 0,   drift: 12, rotate: 10 },
  { Icon: Glasses,        top: '86%', left: '78%',  size: 56, opacity: 0.46, color: 'text-slate-500', duration: 10, delay: 1.5, drift: 14, rotate: 6 },
  { Icon: ShoppingBag,    top: '90%', left: '34%',  size: 60, opacity: 0.42, color: 'text-slate-500', duration: 13, delay: 0.8, drift: 16, rotate: 4 },
  { Icon: Wand2,          top: '30%', left: '94%',  size: 48, opacity: 0.52, color: 'text-cyan-500',  duration: 8,  delay: 0.3, drift: 12, rotate: 12 },
  { Icon: SwatchBook,     top: '68%', left: '66%',  size: 50, opacity: 0.44, color: 'text-amber-500', duration: 12, delay: 2.2, drift: 14, rotate: 6 },
  { Icon: PersonStanding, top: '68%', left: '92%',  size: 76, opacity: 0.38, color: 'text-slate-500', duration: 16, delay: 1.2, drift: 18, rotate: 4 },
];

const MahiFashionBackdrop = () => {
  const reduceMotion = useReducedMotion();

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {GLYPHS.map(({ Icon, top, left, size, opacity, color, duration, delay, drift, rotate }, i) => (
        <motion.div
          key={i}
          className={`absolute ${color}`}
          style={{ top, left, opacity }}
          animate={reduceMotion ? undefined : {
            y: [0, -drift, 0],
            rotate: [0, rotate, 0],
          }}
          transition={{ duration, delay, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Icon size={size} strokeWidth={1.5} />
        </motion.div>
      ))}
      {/* white scrim — keeps the glyphs as texture, not content */}
      <div className="absolute inset-0 bg-white/30" />
    </div>
  );
};

export default MahiFashionBackdrop;
